import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { getCompanyToday } from './wpaDateUtils';

/**
 * 抓取 WPA 送貨日不可選清單（車期／公眾假期／截單已過），供日曆 disabledDate 使用。
 *
 * 設計重點：
 * - 使用者「可選上限」= 今天 + `selectableMonths`（預設 6，固定，不受翻月影響）。超過者一律 disabled。
 * - API 抓取範圍 = [今天 -7 日, 可選上限 +7 日]；兩端各 7 日緩衝僅供上下月面板溢出格拿 reason 做灰化，**不開放實際選到**。
 * - 可呼叫 `extendPrefetchTo(date)` 在使用者翻到較遠月份時**只擴充 API 抓取**；不會提高可選上限。
 *
 * 後端：GET /api/wpa-commerce/delivery-date-availability
 *
 * @param {object} params
 * @param {string|number|null|undefined} params.customerId 指定客戶（無時用 WPA 登入預設客戶）
 * @param {number} [params.selectableMonths=6] 使用者可選上限的月份數（今天起算）
 * @param {boolean} [params.enabled=true] false 時暫停所有 fetch（資料尚未就緒時使用，避免以 undefined customerId 發出無效請求）
 * @returns {{
 *   unavailableMap: Map<string, { reason: string, message: string }>,
 *   loading: boolean,
 *   reload: () => void,
 *   disabledDate: (d: dayjs.Dayjs) => boolean,
 *   getReason: (d: dayjs.Dayjs) => { reason: string, message: string } | null,
 *   extendPrefetchTo: (date: dayjs.Dayjs | Date | string) => void,
 * }}
 */
export function useWpaDeliveryAvailability({ customerId, selectableMonths = 6, enabled = true } = {}) {
  const [unavailableMap, setUnavailableMap] = useState(() => new Map());
  const [loading, setLoading] = useState(false);
  const [fetchedRange, setFetchedRange] = useState({ from: null, to: null });
  /** 上次成功完成的 fetch 所用的 customerId（undefined 表示從未成功過）。 */
  const [fetchedCustomerId, setFetchedCustomerId] = useState(undefined);
  const inflightRef = useRef(null);

  /** 固定的「可選上限」：今天（公司時區）+ selectableMonths 個月。不會被 `extendPrefetchTo` 改動。 */
  const selectableUntil = useMemo(
    () => getCompanyToday().add(Math.max(1, selectableMonths), 'month'),
    // 以 selectableMonths 與當日 key 為依賴；避免每 render 都是新物件。
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectableMonths, getCompanyToday().valueOf()],
  );

  /** API 抓取目標上限（可被擴充）；初值 = selectableUntil + 7 日 */
  const [apiTo, setApiTo] = useState(() => selectableUntil.add(7, 'day'));

  const today = getCompanyToday();
  const rangeFrom = today.subtract(7, 'day');
  const rangeTo = apiTo;

  const needsFetch =
    enabled && (
      !fetchedRange.from ||
      !fetchedRange.to ||
      rangeFrom.isBefore(fetchedRange.from, 'day') ||
      rangeTo.isAfter(fetchedRange.to, 'day') ||
      // customerId 改變（含 undefined→真實值）時必須重抓，否則無客戶的結果會當成有客戶的用。
      String(customerId ?? '') !== String(fetchedCustomerId ?? '')
    );

  const fetchAvailability = useCallback(
    async (fromD, toD, custId) => {
      const token = localStorage.getItem('wpa_token');
      if (!token) return;
      const from = fromD.format('YYYY-MM-DD');
      const to = toD.format('YYYY-MM-DD');
      const key = `${custId || ''}|${from}|${to}`;
      if (inflightRef.current === key) return;
      inflightRef.current = key;
      setLoading(true);
      try {
        const qs = new URLSearchParams({ fromDate: from, toDate: to });
        if (custId) qs.set('customerId', String(custId));
        const res = await fetch(`/api/wpa-commerce/delivery-date-availability?${qs.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          setUnavailableMap(new Map());
          return;
        }
        const json = await res.json().catch(() => ({}));
        const list = Array.isArray(json?.unavailable) ? json.unavailable : [];
        const m = new Map();
        list.forEach((x) => {
          if (x && x.date) {
            m.set(String(x.date).slice(0, 10), { reason: x.reason || '', message: x.message || '' });
          }
        });
        // 以後端實際回傳的 fromDate/toDate 為權威；後端可能截短（例如早期 186 天上限）。
        // 如果回傳的 toDate 比我們請求的 toD 早，後續 disabledDate 將該日期當「區間外」保守 disable，
        // 避免出現「backend 根本沒判斷 → 前端誤可選」的漏洞。
        const respFromStr = typeof json?.fromDate === 'string' ? json.fromDate.slice(0, 10) : '';
        const respToStr = typeof json?.toDate === 'string' ? json.toDate.slice(0, 10) : '';
        const effectiveFrom = respFromStr ? dayjs(respFromStr) : fromD;
        const effectiveTo = respToStr ? dayjs(respToStr) : toD;
        try {
          // eslint-disable-next-line no-console
          console.log('[WpaDeliveryAvailability] fetch result', {
            url: `/api/wpa-commerce/delivery-date-availability?${qs.toString()}`,
            custId,
            requestedFrom: from,
            requestedTo: to,
            serverFrom: respFromStr,
            serverTo: respToStr,
            truncated: respToStr && respToStr < to,
            status: res.status,
            totalUnavailable: list.length,
          });
        } catch (_) { /* log only; ignore */ }
        // 若在這次 fetch 期間已有更新的請求啟動（key 被覆蓋），丟棄這個過期回應，
        // 避免舊的 null-customerId 結果在新的 customerId 結果之後完成並覆蓋正確資料。
        if (inflightRef.current !== key) return;
        setUnavailableMap(m);
        setFetchedRange({ from: effectiveFrom, to: effectiveTo });
        setFetchedCustomerId(custId ?? null);
      } finally {
        setLoading(false);
        if (inflightRef.current === key) inflightRef.current = null;
      }
    },
    [],
  );

  const reload = useCallback(() => {
    // 清除 fetchedCustomerId 以強制 needsFetch = true，確保下一次 effect 一定重抓。
    setFetchedCustomerId(undefined);
    fetchAvailability(rangeFrom, rangeTo, customerId);
  }, [fetchAvailability, rangeFrom, rangeTo, customerId]);

  useEffect(() => {
    if (needsFetch) {
      fetchAvailability(rangeFrom, rangeTo, customerId);
    }
    // 手動以 valueOf 做依賴，避免每次 render 產生新 dayjs 造成無限觸發。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, customerId, rangeFrom.valueOf(), rangeTo.valueOf()]);

  const getReason = useCallback(
    (d) => {
      if (!d) return null;
      const key = dayjs(d).format('YYYY-MM-DD');
      return unavailableMap.get(key) || null;
    },
    [unavailableMap],
  );

  const disabledDate = useCallback(
    (d) => {
      if (!d) return true;
      const cur = dayjs(d).startOf('day');
      // 今天（公司時區）之前不可選（與後端 ValidateAsync 的截單保守一致）。
      if (cur.isBefore(getCompanyToday(), 'day')) return true;
      // 超過可選上限（今天 + selectableMonths）→ 一律不可選。
      if (cur.isAfter(selectableUntil, 'day')) return true;
      const from = fetchedRange.from;
      const to = fetchedRange.to;
      // 尚無任何成功載入的區間：今天起一律不可選。
      if (!from || !to) return true;
      // 落在已抓取區間外（例如使用者翻到的更遠月份尚未回應）→ 先不可選。
      if (cur.isAfter(dayjs(to).startOf('day'), 'day') || cur.isBefore(dayjs(from).startOf('day'), 'day')) {
        return true;
      }
      const reason = getReason(d);
      return !!reason;
    },
    [getReason, fetchedRange.from, fetchedRange.to, selectableUntil],
  );

  /**
   * 使用者翻到較遠月份時，**只擴充 API 抓取區間**（供該月灰化顯示），不會提高可選上限。
   * 若想提高可選上限，請調整 hook 的 `selectableMonths`。
   */
  const extendPrefetchTo = useCallback(
    (d) => {
      if (!d) return;
      const target = dayjs(d).endOf('month').add(7, 'day');
      setApiTo((prev) => (target.isAfter(prev, 'day') ? target : prev));
    },
    [],
  );

  return {
    unavailableMap,
    loading,
    reload,
    disabledDate,
    getReason,
    extendPrefetchTo,
  };
}

export default useWpaDeliveryAvailability;
